// Finds pokemons and generations missing in the pokedex.
// Species data (name, generation, types, family, gender, legendary/mythical) comes from the PokeAPI CSV files on GitHub.
// Availability in Pokemon GO (released, shiny, shadow) can optionally be taken from pogoapi.net.
import { pad } from '@/utils/common.js';

const POKEAPI_CSV = 'https://raw.githubusercontent.com/PokeAPI/pokeapi/master/data/v2/csv';
const POGOAPI = 'https://pogoapi.net/api/v1';
const ENGLISH_LANGUAGE_ID = '9';

const REGIONS = {
  1: 'Kanto',
  2: 'Johto',
  3: 'Hoenn',
  4: 'Sinnoh',
  5: 'Unova',
  6: 'Kalos',
  7: 'Alola',
  8: 'Galar',
  9: 'Paldea',
};
const GENERATION_COLORS = ['#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4', '#42d4f4', '#f032e6'];
const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

export const AVAILABILITY_FLAGS = ['collectable', 'shiny', 'purified'];

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

async function fetchText(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText} for ${url}`);
  }
  return response.text();
}

// Minimal CSV parser that handles quoted values
function parseCsv(text) {
  const rows = [];
  let row = [];
  let value = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') {
        value += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        value += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(value);
      value = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i++;
      row.push(value);
      rows.push(row);
      row = [];
      value = '';
    } else {
      value += char;
    }
  }
  if (value || row.length) {
    row.push(value);
    rows.push(row);
  }
  const [header, ...data] = rows.filter(r => r.length > 1 || r[0]);
  return data.map(r => {
    const entry = {};
    header.forEach((key, index) => (entry[key] = r[index]));
    return entry;
  });
}

async function fetchCsv(name) {
  return parseCsv(await fetchText(`${POKEAPI_CSV}/${name}.csv`));
}

export async function fetchSpecies() {
  const [species, names, pokemon, pokemonTypes, types] = await Promise.all(
    ['pokemon_species', 'pokemon_species_names', 'pokemon', 'pokemon_types', 'types'].map(fetchCsv)
  );

  const englishNames = {};
  names
    .filter(n => n.local_language_id === ENGLISH_LANGUAGE_ID)
    .forEach(n => (englishNames[n.pokemon_species_id] = n.name));

  const typeNames = {};
  types.forEach(t => (typeNames[t.id] = capitalize(t.identifier)));

  // Types of the default form of each species
  const speciesOfDefaultPokemon = {};
  pokemon.filter(p => p.is_default === '1').forEach(p => (speciesOfDefaultPokemon[p.id] = p.species_id));
  const speciesTypes = {};
  pokemonTypes.forEach(t => {
    const speciesId = speciesOfDefaultPokemon[t.pokemon_id];
    if (!speciesId) return;
    speciesTypes[speciesId] = speciesTypes[speciesId] || [];
    speciesTypes[speciesId][Number(t.slot) - 1] = typeNames[t.type_id];
  });

  const byId = {};
  species.forEach(s => (byId[s.id] = s));

  const baseOfChain = s => (s.evolves_from_species_id ? baseOfChain(byId[s.evolves_from_species_id]) : s);
  const evolutionStage = s => (s.evolves_from_species_id ? evolutionStage(byId[s.evolves_from_species_id]) + 1 : 1);

  return species.map(s => {
    const genderRate = Number(s.gender_rate); // -1 genderless, 0 always male, 8 always female
    const [type1, type2] = speciesTypes[s.id] || [];
    const base = baseOfChain(s);
    return {
      id: Number(s.id),
      generation: Number(s.generation_id),
      name: englishNames[s.id] || capitalize(s.identifier),
      family: englishNames[base.id] || capitalize(base.identifier),
      evolutionNumber: String(evolutionStage(s)),
      type1: type1 || '',
      type2: type2 || '',
      legendary: s.is_mythical === '1' ? 'Mythical' : s.is_legendary === '1' ? 'Legendary' : '',
      male: genderRate >= 0 && genderRate < 8,
      female: genderRate > 0,
    };
  });
}

// pogoapi.net returns objects keyed by pokedex number, or arrays of objects with an id
async function fetchPogoapiIds(file) {
  const data = JSON.parse(await fetchText(`${POGOAPI}/${file}`));
  const entries = Array.isArray(data) ? data : Object.keys(data).map(key => ({ key, ...data[key] }));
  return new Set(entries.map(entry => Number(entry.id || entry.pokemon_id || entry.key)).filter(Boolean));
}

// Pokedex numbers per availability flag
export async function fetchAvailability() {
  const [collectable, shiny, purified] = await Promise.all(
    ['released_pokemon.json', 'shiny_pokemon.json', 'shadow_pokemon.json'].map(fetchPogoapiIds)
  );
  return { collectable, shiny, purified };
}

function newGeneration(generationNumber, existingGenerations) {
  const existing = Object.values(existingGenerations);
  const latest = [...existing]
    .sort((g1, g2) => String(g1.id).localeCompare(String(g2.id), 'en', { numeric: true }))
    .pop();
  // Match the style of the existing generation numbers (1 / "1" / "I")
  let number = generationNumber;
  if (latest && typeof latest.number === 'string') {
    number = /^[IVX]+$/.test(latest.number) ? ROMAN[generationNumber] : String(generationNumber);
  }
  const usedColors = existing.map(g => g.color);
  return {
    id: `gen${generationNumber}`,
    number,
    name: REGIONS[generationNumber] || `Gen ${generationNumber}`,
    color: GENERATION_COLORS.find(c => !usedColors.includes(c)) || 'grey',
  };
}

// Compares species (and optionally availability) with the current pokedex. Nothing is written.
// New pokemons are not collectable unless availability says so, so they stay hidden until marked as released.
export function planSync({ species, availability, pokedex, generations }) {
  pokedex = pokedex || {};
  generations = generations || {};

  const existingNumbers = new Set();
  Object.keys(pokedex).forEach(key => {
    existingNumbers.add(Number(key));
    existingNumbers.add(Number(pokedex[key].number));
  });

  const newGenerations = {};
  for (const generationNumber of new Set(species.map(s => s.generation))) {
    const id = `gen${generationNumber}`;
    if (!generations[id]) {
      newGenerations[id] = newGeneration(generationNumber, { ...generations, ...newGenerations });
    }
  }

  const newPokemons = species
    .filter(s => !existingNumbers.has(s.id))
    .map(s => ({
      number: pad(s.id),
      evolutionNumber: s.evolutionNumber,
      name: s.name,
      family: s.family,
      generation: `gen${s.generation}`,
      type1: s.type1,
      type2: s.type2,
      legendary: s.legendary,
      collectable: availability ? availability.collectable.has(s.id) : false,
      male: s.male,
      female: s.female,
      lucky: s.legendary !== 'Mythical',
      shiny: availability ? availability.shiny.has(s.id) : false,
      purified: availability ? availability.purified.has(s.id) : false,
    }));

  // Flags on existing pokemons that are now available. Flags are only turned on, never off.
  const flagChanges = [];
  if (availability) {
    for (const key of Object.keys(pokedex)) {
      const pokemon = pokedex[key];
      const number = Number(pokemon.number || key);
      for (const flag of AVAILABILITY_FLAGS) {
        if (!pokemon[flag] && availability[flag].has(number)) {
          flagChanges.push({ key, number: pokemon.number || key, name: pokemon.name, flag });
        }
      }
    }
  }

  return { newGenerations, newPokemons, flagChanges };
}

// Multi-path update for the selected parts of a plan. Generations are only added when a selected pokemon needs them.
export function buildUpdates({ newGenerations, newPokemons, flagChanges }) {
  const updates = {};
  for (const pokemon of newPokemons) {
    updates[`pokedex/${pokemon.number}`] = pokemon;
    if (newGenerations[pokemon.generation]) {
      updates[`generations/${pokemon.generation}`] = newGenerations[pokemon.generation];
    }
  }
  for (const change of flagChanges) {
    updates[`pokedex/${change.key}/${change.flag}`] = true;
  }
  return updates;
}
